/**
 * Accessible Reading Order — helpers for the accessibility overlay export.
 *
 * Reading order is a persisted, ordered list of element IDs covering
 * text-bearing elements (type 'text' | 'button'). It lives at
 * canvasState.readingOrder (additive JSONB key — no migration).
 *
 * - computeDefaultReadingOrder: visual order (top-to-bottom, then
 *   left-to-right) used when a project has no persisted order yet.
 * - reconcileReadingOrder: prunes deleted element IDs from a persisted
 *   order and appends newly added text elements (in default-order
 *   position among themselves), reporting which IDs were appended so the
 *   Reading Order modal (el-ax-2) can flag them for the designer.
 *
 * Epic: cwt-containment-accessible-reading-order (el-ax-1).
 */

import type { CanvasElement } from '../types';

/** Row-band tolerance: elements whose y differs by less than this are
 *  treated as the same visual row and ordered left-to-right. */
const ROW_TOLERANCE_PX = 10;

export function isReadableElement(el: CanvasElement): boolean {
  return el.type === 'text' || el.type === 'button';
}

/**
 * Default visual reading order: top-to-bottom, left-to-right.
 * Only text and button elements participate. Hidden elements are
 * INCLUDED here (order is stable designer data); visibility filtering
 * happens at emission time in the export engine.
 */
export function computeDefaultReadingOrder(elements: CanvasElement[]): string[] {
  const readable = elements.filter(isReadableElement);
  const sorted = [...readable].sort((a, b) => {
    if (Math.abs(a.y - b.y) > ROW_TOLERANCE_PX) return a.y - b.y;
    if (a.x !== b.x) return a.x - b.x;
    return a.id.localeCompare(b.id); // deterministic tiebreak
  });
  return sorted.map((el) => el.id);
}

export interface ReconciledReadingOrder {
  order: string[];
  /** IDs appended this reconciliation (new text elements since the order
   *  was last persisted). el-ax-2's modal flags these visually. */
  appendedIds: string[];
}

/**
 * Reconcile a persisted order against the current element list:
 * 1. Drop IDs that no longer exist or are no longer readable.
 * 2. Append readable elements missing from the order, positioned among
 *    themselves by default visual order.
 * Passing undefined/empty persisted order yields the default order with
 * every ID reported as appended (first-run signal for the modal).
 */
export function reconcileReadingOrder(
  persisted: string[] | undefined,
  elements: CanvasElement[]
): ReconciledReadingOrder {
  const readableIds = new Set(elements.filter(isReadableElement).map((el) => el.id));

  const kept = (persisted ?? []).filter((id) => readableIds.has(id));
  const keptSet = new Set(kept);

  const defaultOrder = computeDefaultReadingOrder(elements);
  const appendedIds = defaultOrder.filter((id) => !keptSet.has(id));

  return { order: [...kept, ...appendedIds], appendedIds };
}
